import { memo } from 'react';
import { motion } from 'framer-motion';

const RINGS = 5;

/**
 * The Business Brain hub. Rendered as an SVG <g> centred at (0,0); the Wheel
 * positions it at the wheel centre. Concentric "memory" rings fill as the loop
 * runs, so the Brain visibly densifies. Isolated + memoized per perf rules.
 */
export const BrainCore = memo(function BrainCore({
  fill,
  reducedMotion,
  r = 76,
}: {
  fill: number;
  reducedMotion: boolean;
  r?: number;
}) {
  const filled = Math.round(Math.min(1, Math.max(0, fill)) * RINGS);

  return (
    <g>
      {/* soft glow that intensifies as the Brain fills */}
      <motion.circle
        r={r * 1.55}
        fill="url(#fw-brain-glow)"
        style={{ opacity: 0.16 + fill * 0.42 }}
        animate={reducedMotion ? undefined : { scale: [1, 1.06, 1] }}
        transition={{ repeat: Infinity, duration: 3.4, ease: 'easeInOut' }}
      />

      {/* memory rings — accumulate outward */}
      {Array.from({ length: RINGS }).map((_, i) => {
        const rr = r * 0.42 + (i / (RINGS - 1)) * (r * 0.5);
        const isFilled = i < filled;
        return (
          <circle
            key={i}
            r={rr}
            fill="none"
            stroke={isFilled ? 'var(--fd-teal)' : 'var(--fd-text)'}
            strokeOpacity={isFilled ? 0.65 : 0.1}
            strokeWidth={isFilled ? 2 : 1}
            style={{ transition: 'stroke-opacity .55s ease, stroke .55s ease, stroke-width .55s ease' }}
          />
        );
      })}

      {/* faceted core */}
      <circle r={r * 0.4} fill="var(--fd-paper)" stroke="var(--fd-text)" strokeOpacity={0.85} strokeWidth={1.5} />
      <circle
        r={r * 0.4}
        fill="none"
        stroke="var(--fd-accent)"
        strokeOpacity={0.18 + fill * 0.5}
        strokeWidth={1.5}
        style={{ transition: 'stroke-opacity .55s ease' }}
      />

      {/* live core spark */}
      <motion.circle
        r={5 + fill * 5}
        fill="var(--fd-accent)"
        animate={reducedMotion ? undefined : { scale: [1, 1.22, 1] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
        style={{ transition: 'r .55s ease' }}
      />
    </g>
  );
});
